import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@reflex/ui/components/ui/button';
import { Input } from '@reflex/ui/components/ui/input';
import { SecretInput } from '@reflex/ui/components/forms/SecretInput';
import { Label } from '@reflex/ui/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@reflex/ui/components/ui/alert';
import { Badge } from '@reflex/ui/components/ui/badge';
import { PageTitle, PageDescription, PanelTitle } from '@reflex/ui/components/layout/Typography';
import { WizardLayout } from '@reflex/ui/components/layout/WizardLayout';
import { setupApi, SetupApiError, type SetupAuthProvider } from '@/lib/setup-api';
import { useAuth } from '@/hooks/auth/useAuth';
import { DomainEditor } from '@/components/auth/DomainEditor';
import { getAuthProviderForm } from '@/lib/plugin-registry';
import { selectSignInProviders } from '@/lib/auth-providers';
import { toast } from 'sonner';
import { WizardStepper, type WizardStepperStage } from '@/components/setup/WizardStepper';
import { CopyRow } from '@/components/setup/CopyRow';
import { SignInProviderRow } from '@/components/setup/SignInProviderRow';

// The email/password plugin contributes its own self-contained sign-in form
// through the registry — the same slot the login/signup screens render. The
// provider key is fixed, so resolve it once at module scope (a stable
// reference, unlike a per-render lookup).
const EmailAuthForm = getAuthProviderForm('email');

/**
 * Phase 1 (system) setup wizard stages. Phase 1 happens once per service
 * and stops at "promote the signing-in user to super-admin". All
 * org-related setup (plugin selection, sandbox key, integrations) lives
 * in the authed phase 2 wizard at `/orgs/:orgSlug/setup` and
 * `/org-setup/new`.
 */
type WizardStage =
  | 'admission'
  | 'admin-account'
  | 'auth'
  | 'sign-in'
  | 'domains'
  | 'finalize'
  | 'done';

export function SetupPage() {
  const [manualStage, setManualStage] = useState<WizardStage | null>(null);
  const auth = useAuth();

  const stateQuery = useQuery({
    queryKey: ['setup', 'state'],
    queryFn: () => setupApi.getState(),
  });

  const providersQuery = useQuery({
    queryKey: ['setup', 'auth-providers'],
    queryFn: () => setupApi.listAuthProviders(),
    enabled: stateQuery.data?.setupRequired === true,
  });

  // In dev the admission step is trivial: mint a setup_session cookie
  // silently so privileged setup routes accept the wizard's subsequent
  // calls. Once it succeeds we refetch /setup/state, which flips
  // admissionCompleted=true and lets `derivedStage` advance past admission.
  // Use a ref to guarantee the network call only fires once per mount.
  const devVerifyAttempted = useRef(false);
  useEffect(() => {
    const state = stateQuery.data;
    if (!state) return;
    if (devVerifyAttempted.current) return;
    if (state.isProduction || state.admissionCompleted || !state.setupRequired) return;
    devVerifyAttempted.current = true;
    void setupApi.verifyToken('').then(() => stateQuery.refetch());
  }, [stateQuery]);

  const emailAuthAvailable = stateQuery.data?.emailAuthAvailable ?? false;

  const derivedStage = useMemo<WizardStage>(() => {
    const state = stateQuery.data;
    if (!state) return 'admission';
    if (!state.setupRequired) return 'done';
    if (!state.admissionCompleted) return 'admission';
    // Already signed in (e.g. just created the admin, or an OAuth sign-in
    // round-trip completed): jump to the allowlist step before finalizing.
    if (auth.authenticated) return 'domains';
    // Email-admin path takes priority: when the email-auth plugin is active
    // and no admin exists yet, create one with email + password. This is
    // the OAuth-optional onboarding route.
    if (state.emailAuthAvailable && !state.adminAccountExists) return 'admin-account';
    const providers = providersQuery.data?.providers ?? [];
    // With email auth active the operator can always reach the sign-in step;
    // OAuth configuration is optional, not a gate.
    if (!providersQuery.data && !state.emailAuthAvailable) return 'admission';
    if (providers.length > 0 && !providers.some((p) => p.configured) && !state.emailAuthAvailable) {
      return 'auth';
    }
    return 'sign-in';
  }, [stateQuery.data, providersQuery.data, auth.authenticated]);

  const stage = manualStage ?? derivedStage;
  const setStage = setManualStage;

  if (stateQuery.isLoading) {
    return (
      <WizardLayout centered className="text-muted-foreground">
        Preparing setup wizard&hellip;
      </WizardLayout>
    );
  }

  if (stateQuery.error) {
    return (
      <WizardLayout centered>
        <Alert variant="destructive" className="max-w-md">
          <AlertTitle>Unable to load setup state</AlertTitle>
          <AlertDescription>{String(stateQuery.error)}</AlertDescription>
        </Alert>
      </WizardLayout>
    );
  }

  const state = stateQuery.data!;

  const wizardStages = emailAuthAvailable ? WIZARD_STAGES_WITH_ADMIN : WIZARD_STAGES;

  return (
    <WizardLayout eyebrow="Reflex setup">
      <WizardStepper
        stages={wizardStages}
        current={stage}
        furthest={derivedStage}
        onSelect={setStage}
      />

      {stage === 'admission' && (
        <AdmissionStage
          isProduction={state.isProduction}
          alreadyAdmitted={state.admissionCompleted}
          onAdmitted={() => setStage(emailAuthAvailable ? 'admin-account' : 'auth')}
        />
      )}
      {stage === 'admin-account' && (
        <AdminAccountStage
          onCreated={async () => {
            await auth.refreshSession();
            await stateQuery.refetch();
            setStage('domains');
          }}
        />
      )}
      {stage === 'auth' && (
        <AuthStage emailAuthAvailable={emailAuthAvailable} onNext={() => setStage('sign-in')} />
      )}
      {stage === 'sign-in' && (
        <SignInStage
          emailAuthAvailable={emailAuthAvailable}
          onNext={() => setStage('domains')}
          onAuthenticated={async () => {
            await auth.refreshSession();
            setStage('domains');
          }}
        />
      )}
      {stage === 'domains' && <DomainsStage onNext={() => setStage('finalize')} />}
      {stage === 'finalize' && <FinalizeStage onDone={() => setStage('done')} />}
      {stage === 'done' && <DoneStage />}
    </WizardLayout>
  );
}

function StageHeader({
  title,
  description,
  badge,
}: {
  title: React.ReactNode;
  description?: React.ReactNode;
  badge?: React.ReactNode;
}) {
  return (
    <header className="mb-6">
      <div className="flex flex-wrap items-center gap-3">
        <PageTitle>{title}</PageTitle>
        {badge}
      </div>
      {description && <PageDescription>{description}</PageDescription>}
    </header>
  );
}

const WIZARD_STAGES: ReadonlyArray<WizardStepperStage<WizardStage>> = [
  { id: 'admission', label: 'Admission' },
  { id: 'auth', label: 'Sign-in' },
  { id: 'sign-in', label: 'Log in' },
  { id: 'domains', label: 'Domains' },
  { id: 'finalize', label: 'Finalize' },
  { id: 'done', label: 'Done' },
];

/** Stepper variant shown when the email-auth plugin offers the admin step. */
const WIZARD_STAGES_WITH_ADMIN: ReadonlyArray<WizardStepperStage<WizardStage>> = [
  { id: 'admission', label: 'Admission' },
  { id: 'admin-account', label: 'Admin' },
  { id: 'auth', label: 'Providers' },
  { id: 'sign-in', label: 'Log in' },
  { id: 'domains', label: 'Domains' },
  { id: 'finalize', label: 'Finalize' },
  { id: 'done', label: 'Done' },
];

const ADMIN_ACCOUNT_DEFAULTS = { email: 'admin@example.com', name: 'Admin', password: 'password' };

const adminAccountSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Enter a valid email address'),
  name: z.string().trim().max(120).optional(),
  password: z.string().min(8, 'Password must be at least 8 characters'),
});
type AdminAccountValues = z.infer<typeof adminAccountSchema>;

/**
 * Onboarding-only step that creates the first email/password administrator.
 * Prefilled with sensible defaults (`admin@example.com` / `password`) the
 * operator is expected to change. On success the server promotes the account
 * to super-admin and signs the operator in.
 */
function AdminAccountStage({ onCreated }: { onCreated: () => void | Promise<void> }) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AdminAccountValues>({
    resolver: zodResolver(adminAccountSchema),
    defaultValues: ADMIN_ACCOUNT_DEFAULTS,
  });

  const mutation = useMutation({
    mutationFn: (values: AdminAccountValues) =>
      setupApi.createAdminAccount({
        email: values.email,
        password: values.password,
        name: values.name,
      }),
    onSuccess: async () => {
      toast.success('Administrator account created');
      await onCreated();
    },
    onError: (err) =>
      toast.error('Could not create the admin account', {
        description: err instanceof SetupApiError ? err.message : String(err),
      }),
  });

  return (
    <>
      <StageHeader
        title="Create your administrator account"
        description="This account is your service super-admin. You will sign in with this email and password. You can change these defaults now, and turn on public email registration later from the plugin's admin page."
      />
      <form
        className="max-w-md space-y-4"
        data-testid="setup-admin-account-form"
        onSubmit={handleSubmit((values) => mutation.mutate(values))}
      >
        <div className="space-y-2">
          <Label htmlFor="setup-admin-email">Email</Label>
          <Input id="setup-admin-email" type="email" autoComplete="email" {...register('email')} />
          {errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}
        </div>
        <div className="space-y-2">
          <Label htmlFor="setup-admin-name">Name (optional)</Label>
          <Input id="setup-admin-name" type="text" autoComplete="name" {...register('name')} />
          {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
        </div>
        <div className="space-y-2">
          <Label htmlFor="setup-admin-password">Password</Label>
          <Input
            id="setup-admin-password"
            type="password"
            autoComplete="new-password"
            {...register('password')}
          />
          {errors.password && <p className="text-xs text-destructive">{errors.password.message}</p>}
        </div>
        <Button type="submit" disabled={mutation.isPending}>
          {mutation.isPending ? 'Creating…' : 'Create admin and continue'}
        </Button>
      </form>
    </>
  );
}

function AdmissionStage({
  isProduction,
  alreadyAdmitted,
  onAdmitted,
}: {
  isProduction: boolean;
  alreadyAdmitted: boolean;
  onAdmitted: () => void;
}) {
  const [token, setToken] = useState('');
  const mutation = useMutation({
    mutationFn: (t: string) => setupApi.verifyToken(t),
    onSuccess: () => onAdmitted(),
    onError: (err: Error) => toast.error(err.message),
  });

  if (!isProduction) {
    return (
      <>
        <StageHeader
          title="Admission"
          description={
            <>
              Setup admission requires a one-time{' '}
              <code className="rounded bg-muted px-1 py-0.5 text-xs">SETUP_TOKEN</code> in
              production. In development this step is skipped automatically. Continue to the next
              step whenever you're ready.
            </>
          }
          badge={
            <Badge variant="outline" data-testid="setup-admission-dev-mode">
              Dev mode
            </Badge>
          }
        />
        <Button onClick={onAdmitted} data-testid="setup-admission-continue">
          Continue
        </Button>
      </>
    );
  }

  if (alreadyAdmitted) {
    return (
      <>
        <StageHeader
          title="Setup token verified"
          description="Your setup session is active. Continue to the next step."
          badge={
            <Badge variant="outline" data-testid="setup-admission-already-admitted">
              Already admitted
            </Badge>
          }
        />
        <Button onClick={onAdmitted} data-testid="setup-admission-continue">
          Continue
        </Button>
      </>
    );
  }

  return (
    <>
      <StageHeader
        title="Paste your setup token"
        description={
          <>
            When Reflex boots for the first time in production it prints a one-time{' '}
            <code className="rounded bg-muted px-1 py-0.5 text-xs">SETUP_TOKEN</code> to the server
            logs. Paste it below to admit the wizard.
          </>
        }
      />
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="setup-token">Setup token</Label>
          <Input
            id="setup-token"
            autoComplete="off"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="Paste the long hex token from your server logs"
          />
        </div>
        <Button onClick={() => mutation.mutate(token)} disabled={!token || mutation.isPending}>
          {mutation.isPending ? 'Verifying…' : 'Continue'}
        </Button>
      </div>
    </>
  );
}

function AuthStage({
  onNext,
  emailAuthAvailable,
}: {
  onNext: () => void;
  emailAuthAvailable: boolean;
}) {
  const query = useQuery({
    queryKey: ['setup', 'auth-providers'],
    queryFn: () => setupApi.listAuthProviders(),
  });
  const providers = query.data?.providers ?? [];
  const anyConfigured = providers.some((p) => p.configured);
  // OAuth is optional when an email/password admin can sign in.
  const canContinue = anyConfigured || emailAuthAvailable;
  return (
    <>
      <StageHeader
        title="Configure sign-in providers"
        description={
          emailAuthAvailable
            ? 'Optional. You already have an email/password admin, so OAuth providers are not required. Configure any you want teammates to use, or skip ahead.'
            : 'You need at least one OAuth provider so admins and teammates can sign in. Walk through each enabled provider below; Reflex will show the redirect URI for each.'
        }
      />
      <div className="space-y-4">
        {providers.length === 0 && !emailAuthAvailable && (
          <Alert>
            <AlertTitle>No auth plugins enabled</AlertTitle>
            <AlertDescription>
              Enable an auth plugin (plugin-auth-github, plugin-auth-google, …) in your composition
              root and restart the server.
            </AlertDescription>
          </Alert>
        )}
        {providers.map((p) => (
          <AuthProviderRow key={p.name} provider={p} onSaved={() => query.refetch()} />
        ))}
        <div className="flex items-center justify-between pt-4">
          <p className="text-xs text-muted-foreground">
            {emailAuthAvailable
              ? 'OAuth providers are optional.'
              : 'At least one provider must be configured to continue.'}
          </p>
          <Button onClick={onNext} disabled={!canContinue}>
            Continue
          </Button>
        </div>
      </div>
    </>
  );
}

function AuthProviderRow({
  provider,
  onSaved,
}: {
  provider: SetupAuthProvider;
  onSaved: () => void;
}) {
  const [open, setOpen] = useState(!provider.configured);
  const [clientId, setClientId] = useState(provider.clientId ?? '');
  const [clientSecret, setClientSecret] = useState('');
  const [secretRevealed, setSecretRevealed] = useState(false);
  const [allowedDomain, setAllowedDomain] = useState(provider.allowedDomain ?? '');
  const mutation = useMutation({
    mutationFn: () =>
      setupApi.saveAuthProvider(provider.name, {
        clientId,
        clientSecret: clientSecret || undefined,
        allowedDomain: provider.hasAllowedDomain ? allowedDomain : undefined,
      }),
    onSuccess: () => {
      toast.success(`${provider.label} configured`);
      setClientSecret('');
      setSecretRevealed(false);
      onSaved();
    },
    onError: (err: Error) => toast.error(err.message),
  });
  const revealMutation = useMutation({
    mutationFn: () => setupApi.revealAuthProviderSecret(provider.name),
    onSuccess: (res) => {
      setClientSecret(res.clientSecret ?? '');
      setSecretRevealed(true);
    },
    onError: (err: Error) => toast.error('Could not reveal secret', { description: err.message }),
  });
  return (
    <div className="rounded-md border border-border/60 p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <PanelTitle as="h3">{provider.label}</PanelTitle>
            {provider.configured ? (
              <Badge variant="default">Configured</Badge>
            ) : (
              <Badge variant="outline">Pending</Badge>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {provider.docsUrl && (
            <a
              href={provider.docsUrl}
              target="_blank"
              rel="noreferrer"
              className="text-xs underline"
            >
              Docs
            </a>
          )}
          <Button variant="outline" size="sm" onClick={() => setOpen((v) => !v)}>
            {open ? 'Hide' : provider.configured ? 'Update' : 'Configure'}
          </Button>
        </div>
      </div>
      {open && (
        <div className="mt-4 space-y-4">
          {provider.instructions && provider.instructions.length > 0 && (
            <ol className="list-decimal space-y-1 pl-6 text-sm text-muted-foreground">
              {provider.instructions.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ol>
          )}

          {provider.redirectUri && (
            <div className="rounded-md bg-muted/40 p-3 text-sm">
              <CopyRow label="Redirect URI" value={provider.redirectUri} />
            </div>
          )}

          {provider.scopes && (
            <p className="text-xs text-muted-foreground">
              Scopes: <code className="rounded bg-muted px-1 py-0.5">{provider.scopes}</code>
            </p>
          )}

          {provider.adminConsoleUrl && (
            <div>
              <Button asChild variant="outline" size="sm">
                <a href={provider.adminConsoleUrl} target="_blank" rel="noreferrer">
                  Open {provider.label} console
                </a>
              </Button>
            </div>
          )}

          <div className="space-y-3 pt-2">
            <div className="space-y-2">
              <Label htmlFor={`auth-${provider.name}-client-id`}>Client ID</Label>
              <Input
                id={`auth-${provider.name}-client-id`}
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor={`auth-${provider.name}-client-secret`}>Client secret</Label>
              <div className="flex gap-2">
                <SecretInput
                  id={`auth-${provider.name}-client-secret`}
                  // Revealing fetches the stored secret from the server, so the
                  // surface owns the affordance and drives the masking.
                  reveal={false}
                  revealed={secretRevealed}
                  value={clientSecret}
                  onChange={(e) => {
                    setClientSecret(e.target.value);
                    if (secretRevealed) setSecretRevealed(false);
                  }}
                  placeholder={
                    provider.hasClientSecret ? '•••••• (leave blank to keep current)' : ''
                  }
                />
                {provider.hasClientSecret && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (secretRevealed) {
                        setClientSecret('');
                        setSecretRevealed(false);
                      } else {
                        revealMutation.mutate();
                      }
                    }}
                    disabled={revealMutation.isPending}
                    aria-label={
                      secretRevealed
                        ? `Hide ${provider.label} client secret`
                        : `Reveal ${provider.label} client secret`
                    }
                  >
                    {revealMutation.isPending ? 'Loading…' : secretRevealed ? 'Hide' : 'Reveal'}
                  </Button>
                )}
              </div>
            </div>
            {provider.hasAllowedDomain && (
              <div className="space-y-2">
                <Label htmlFor={`auth-${provider.name}-allowed-domain`}>
                  Allowed email domain (optional)
                </Label>
                <Input
                  id={`auth-${provider.name}-allowed-domain`}
                  value={allowedDomain}
                  onChange={(e) => setAllowedDomain(e.target.value)}
                  placeholder="example.com"
                />
              </div>
            )}
            <Button
              onClick={() => mutation.mutate()}
              disabled={
                !clientId || (!clientSecret && !provider.hasClientSecret) || mutation.isPending
              }
            >
              {mutation.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function SignInStage({
  onNext,
  onAuthenticated,
  emailAuthAvailable,
}: {
  onNext: () => void;
  onAuthenticated: () => void | Promise<void>;
  emailAuthAvailable: boolean;
}) {
  const { authenticated, providers } = useAuth();
  // The email provider signs in via its contributed form below, not an OAuth
  // row. First-run setup shows every provider: the "hide on login" toggle is
  // an operator choice, and this stage is where the first operator is minted.
  const { oauth: oauthEntries } = selectSignInProviders(providers, true);

  return (
    <>
      <StageHeader
        title="Sign in as the service super admin"
        description="Sign in now with the account you want promoted to super-admin. We'll promote it and finalize setup."
      />
      <div className="space-y-4">
        {authenticated ? (
          <Alert>
            <AlertTitle>Signed in</AlertTitle>
            <AlertDescription>
              You're authenticated. Click continue to finalize setup.
            </AlertDescription>
          </Alert>
        ) : (
          <>
            {emailAuthAvailable && EmailAuthForm && (
              <div className="rounded-md border border-border/60 p-4">
                <PanelTitle as="h3" className="mb-3">
                  Email and password
                </PanelTitle>
                <EmailAuthForm hasOtherProviders={false} onSuccess={onAuthenticated} />
              </div>
            )}
            {oauthEntries.length > 0 && (
              <div className="space-y-3">
                {oauthEntries.map(([name, { label }]) => (
                  <SignInProviderRow
                    key={name}
                    name={name}
                    label={label}
                    rememberReturnPath={false}
                  />
                ))}
              </div>
            )}
            {oauthEntries.length === 0 && !emailAuthAvailable && (
              <Alert>
                <AlertTitle>No providers active</AlertTitle>
                <AlertDescription>
                  Go back and configure at least one sign-in provider before continuing.
                </AlertDescription>
              </Alert>
            )}
          </>
        )}
        <div className="flex justify-end">
          <Button onClick={onNext} disabled={!authenticated}>
            Continue
          </Button>
        </div>
      </div>
    </>
  );
}

function DomainsStage({ onNext }: { onNext: () => void }) {
  const { user } = useAuth();
  const query = useQuery({
    queryKey: ['setup', 'auth-domains'],
    queryFn: () => setupApi.getAuthDomains(),
  });
  const mutation = useMutation({
    mutationFn: ({
      domains,
      mode,
      emails,
    }: {
      domains: string[];
      mode: 'off' | 'allow' | 'deny';
      emails: string[];
    }) => setupApi.saveAuthDomains(domains, mode, emails),
    onSuccess: () => {
      toast.success('Allowed domains saved');
      void query.refetch();
    },
    onError: (err: Error) => toast.error('Failed to save', { description: err.message }),
  });

  const myDomain = useMemo(() => {
    const at = user?.email?.lastIndexOf('@');
    return at != null && at >= 0 ? user!.email.slice(at + 1).toLowerCase() : null;
  }, [user]);

  return (
    <>
      <StageHeader
        title="Restrict sign-in to specific email domains"
        description={
          <>
            Optional. Only users whose email domain matches one of these will be able to sign in via
            any OAuth provider. Leave empty to allow every domain. You can update this list later in{' '}
            <strong>Admin → Auth</strong>.
          </>
        }
      />
      <div className="space-y-4">
        {query.isLoading && (
          <p className="text-sm text-muted-foreground">Loading current allowlist…</p>
        )}
        {query.error && (
          <Alert variant="destructive">
            <AlertTitle>Unable to load allowed domains</AlertTitle>
            <AlertDescription>{String(query.error)}</AlertDescription>
          </Alert>
        )}
        {query.data && (
          <DomainEditor
            key={`${query.data.updatedAt}:${query.data.mode}:${query.data.allowedEmailDomains.join(',')}:${query.data.allowedEmails.join(',')}`}
            initial={query.data.allowedEmailDomains}
            initialEmails={query.data.allowedEmails}
            initialMode={query.data.mode}
            onSave={async (domains, mode, emails) => {
              await mutation.mutateAsync({ domains, mode, emails });
            }}
            isSaving={mutation.isPending}
            title={null}
            description={null}
            bare
            suggestion={
              myDomain
                ? {
                    domain: myDomain,
                    label: `You're signed in as ${user?.email}.`,
                  }
                : undefined
            }
          />
        )}
        <div className="flex items-center justify-between border-t pt-4">
          <p className="text-xs text-muted-foreground">
            Save your changes above before continuing. Skipping leaves the list as-is: every domain
            stays allowed if it's empty.
          </p>
          <Button onClick={onNext} disabled={query.isLoading}>
            Continue
          </Button>
        </div>
      </div>
    </>
  );
}

function FinalizeStage({ onDone }: { onDone: () => void }) {
  const qc = useQueryClient();
  const { refreshSession } = useAuth();
  const mutation = useMutation({
    mutationFn: () => setupApi.finalize(),
    onSuccess: async () => {
      toast.success('Setup complete');
      await qc.invalidateQueries();
      await refreshSession();
      onDone();
    },
    onError: (err: Error) => toast.error(err.message),
  });
  const run = useCallback(() => mutation.mutate(), [mutation]);
  return (
    <>
      <StageHeader
        title="Finalize setup"
        description="Reflex will promote your account to super-admin and lift the setup gate. The next step (creating your first organization) runs against the normal app once the gate lifts."
      />
      <Button onClick={run} disabled={mutation.isPending}>
        {mutation.isPending ? 'Finalizing…' : 'Finalize and launch Reflex'}
      </Button>
    </>
  );
}

function DoneStage() {
  return (
    <>
      <StageHeader
        title="You're all set"
        description="The setup gate is down. The next step (creating your first organization) runs as a normal authed flow."
      />
      <Button onClick={() => window.location.assign('/')}>Launch Reflex</Button>
    </>
  );
}
